// src/pages/customerApp/customerHome.tsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Row, Col, Card, Typography, Button, Space, Avatar, Spin, Empty, Image, List
} from 'antd';
import {
  ThunderboltOutlined,
  RocketOutlined,
  MessageOutlined,
  TrophyOutlined,
  LogoutOutlined,
  ArrowRightOutlined,
  InstagramOutlined,
  AreaChartOutlined,
  FireOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  ReadOutlined,
  UserOutlined,
  GiftOutlined,
  MailOutlined,
  PhoneOutlined,
  LeftOutlined,
  RightOutlined,
  CheckCircleFilled,
  VideoCameraOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useAuth, tokenManager } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { listActivitiesByCustomer, type ActivityDTO } from '../../services/activities';
import { listNews, type NewsDTO as NewsBase } from '../../services/news';
import { listViewsByCustomer } from '../../services/views';
import { listCustomerFiles, downloadCustomerFileBlob, type CustomerFileDTO } from '../../services/customers';
import {
  listLandingPagesForCurrentCustomer,
  listLandingLeadsForCurrentCustomer,
  type LandingLeadDTO,
  type LandingPageDTO,
} from '../../services/landingPages';
import dayjs from 'dayjs';
import './customerHome.css';

const { Title, Text, Paragraph } = Typography;

type NewsDTO = NewsBase & {
  excerpt?: string | null;
  imageUrl?: string | null;
  createdAt?: string | null;
  author?: string | null;
  content?: string | null;
};

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1553532435-93d56d1c8bf2?q=80&w=1200';
const NEWS_SNIPPET_MAX_CHARS = 120;
const LEADS_PER_PAGE = 5;

const truncateNewsContent = (text?: string | null, max = NEWS_SNIPPET_MAX_CHARS): string => {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  const partial = trimmed.slice(0, max);
  const lastSpace = partial.lastIndexOf(' ');
  const base = lastSpace > 0 ? partial.slice(0, lastSpace) : partial;
  return `${base.replace(/[.,;:!?\\s]*$/, '')}...`;
};

const AuthImage: React.FC<{
  src?: string | null;
  alt?: string;
  style?: React.CSSProperties;
  className?: string;
  preview?: boolean;
  height?: number | string;
}> = ({ src, alt, style, className, preview = false, height }) => {
  const [blobUrl, setBlobUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let revoked: string | null = null;
    const abort = new AbortController();
    const load = async () => {
      if (!src) { setBlobUrl(undefined); return; }
      try {
        setLoading(true);
        const token = tokenManager.getAccess?.();
        const res = await fetch(src, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        revoked = url;
      } catch { setBlobUrl(undefined); }
      finally { setLoading(false); }
    };
    load();
    return () => { abort.abort(); if (revoked) URL.revokeObjectURL(revoked); };
  }, [src]);

  if (!blobUrl && loading) {
    return <div className={className} style={{ height: typeof height !== 'undefined' ? height : 240, background: '#f0f2f5', ...style }} />;
  }
  if (!blobUrl) return null;
  return <Image alt={alt} src={blobUrl} preview={preview} className={className} style={style} />;
};

type ViewsChartPoint = { label: string; value: number; key: string };

type LeadListItem = LandingLeadDTO & {
  landingPageId: number;
  landingName?: string | null;
  slug?: string | null;
};

const ViewsTrendChart: React.FC<{ series: ViewsChartPoint[] }> = ({ series }) => {
  const ids = useMemo(() => ({
    line: `views-line-${Math.random().toString(36).slice(2, 8)}`,
    glow: `views-glow-${Math.random().toString(36).slice(2, 8)}`,
  }), []);
  const pathRef = useRef<SVGPathElement | null>(null);
  const shadowRef = useRef<SVGPathElement | null>(null);
  const pointRefs = useRef<Array<SVGGElement | null>>([]);

  const chartData = useMemo(() => {
    if (!series.length) return [];
    return [{ label: '0', value: 0, key: 'baseline' }, ...series];
  }, [series]);

  const width = 100, height = 60, chartPaddingY = 6;
  const chartHeight = height - chartPaddingY * 2;
  const values = chartData.map((p) => p.value);
  const max = values.length ? Math.max(...values) : 0;
  const min = values.length ? Math.min(...values) : 0;
  const padX = chartData.length > 1 ? 8 : 0;
  const availableWidth = width - padX * 2 > 0 ? width - padX * 2 : width;
  const stepCount = Math.max(1, chartData.length - 1);
  const step = availableWidth / stepCount;
  const axisMin = Math.min(0, min);
  const axisMax = Math.max(max, axisMin + 1);
  const axisRange = axisMax - axisMin || 1;

  const valueToY = (value: number) => height - chartPaddingY - ((value - axisMin) / axisRange) * chartHeight;
  const formatCompact = (value: number) => {
    if (!Number.isFinite(value)) return '0';
    if (Math.abs(value) >= 1000) {
      const divided = value / 1000;
      return `${(Math.abs(divided) >= 10 ? divided.toFixed(0) : divided.toFixed(1)).replace(/\.0$/, '')}K`;
    }
    return value.toLocaleString();
  };

  pointRefs.current = [];
  const normalized = chartData.map((point, idx) => ({
    ...point,
    x: chartData.length === 1 ? width / 2 : padX + idx * step,
    y: valueToY(point.value),
  }));

  const buildSmoothPath = (points: typeof normalized) => {
    if (!points.length) return '';
    if (points.length === 1) return `M ${points[0].x},${points[0].y}`;
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1], curr = points[i];
      d += ` Q ${(prev.x + curr.x) / 2},${prev.y} ${curr.x},${curr.y}`;
    }
    return d;
  };
  const pathD = buildSmoothPath(normalized);

  useEffect(() => {
    if (!normalized.length) return;
    [pathRef, shadowRef].forEach(ref => {
      const el = ref.current;
      if (el) {
        const length = el.getTotalLength();
        el.style.transition = 'none';
        el.style.strokeDasharray = `${length}`;
        el.style.strokeDashoffset = `${length}`;
        void el.getBoundingClientRect();
        el.style.transition = 'stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)';
        el.style.strokeDashoffset = '0';
      }
    });
    pointRefs.current.forEach((node, idx) => {
      if (!node) return;
      node.style.transition = 'none';
      node.style.transformOrigin = '50% 50%';
      node.style.opacity = '0';
      node.style.transform = 'translateY(8px) scale(0.7)';
      void node.getBoundingClientRect();
      const delay = 0.25 + idx * 0.06;
      node.style.transition = `transform 0.6s ${delay}s cubic-bezier(0.22,1,0.36,1), opacity 0.6s ${delay}s ease-out`;
      node.style.transform = 'translateY(0) scale(1)';
      node.style.opacity = '1';
    });
  }, [pathD, normalized.length]);

  const tickCount = 5;
  const axisTicks = Array.from({ length: tickCount }, (_, i) => {
    const ratio = (tickCount - 1 - i) / Math.max(1, tickCount - 1);
    const value = axisMin + ratio * axisRange;
    const y = valueToY(value);
    return { value, y, percent: (y / height) * 100 };
  });

  if (!chartData.length) {
    return <div style={{ minHeight: 160, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.02)', borderRadius: 12, padding: 24 }}><Text style={{ color: '#8c8c8c' }}>Nessun dato disponibile</Text></div>;
  }

  const legendWidth = 56;
  return (
    <div style={{ position: 'relative', marginLeft: -legendWidth, paddingLeft: legendWidth, paddingRight: 8, paddingTop: 16, paddingBottom: 24 }}>
      <div style={{ position: 'absolute', left: 0, top: chartPaddingY, bottom: chartPaddingY, width: legendWidth, pointerEvents: 'none' }}>
        {axisTicks.map((tick, idx) => (
          <div key={`tick-${idx}`} style={{ position: 'absolute', right: 0, top: `${tick.percent}%`, transform: 'translateY(-50%)', textAlign: 'right' }}>
            <Text style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>{formatCompact(tick.value)}</Text>
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ width: '100%', height: 180, overflow: 'visible' }}>
        <defs>
          <linearGradient id={ids.line} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#6366f1" /><stop offset="50%" stopColor="#a855f7" /><stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
          <filter id={ids.glow} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feOffset in="blur" dy="4" result="offsetBlur" />
            <feComponentTransfer in="offsetBlur"><feFuncA type="linear" slope="0.35" /></feComponentTransfer>
            <feMerge><feMergeNode in="offsetBlur" /></feMerge>
          </filter>
        </defs>
        {axisTicks.map((tick, idx) => <line key={`grid-h-${idx}`} x1={0} x2={width} y1={tick.y} y2={tick.y} stroke="rgba(148,163,184,0.2)" strokeDasharray="4 6" strokeWidth={0.4} />)}
        <path ref={shadowRef} d={pathD} fill="none" stroke="rgba(99,102,241,0.25)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" filter={`url(#${ids.glow})`} />
        <path ref={pathRef} d={pathD} fill="none" stroke={`url(#${ids.line})`} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        {normalized.map((p, idx) => <g key={p.key} ref={(el) => { pointRefs.current[idx] = el; }}><circle cx={p.x} cy={p.y} r={1.8} fill="#6366f1" /></g>)}
      </svg>
    </div>
  );
};

// ============================================================
// LANDING & LEAD CARD - OTTIMIZZATA
// ============================================================

interface LandingLeadCardProps {
  landingPages: LandingPageDTO[];
  loading: boolean;
}

const LandingLeadCard: React.FC<LandingLeadCardProps> = ({ landingPages, loading }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const requestRef = useRef(0);

  const currentLanding = landingPages[selectedIndex] || null;
  const hasMultipleLandings = landingPages.length > 1;

  const landingById = useMemo(() => {
    const map = new Map<number, LandingPageDTO>();
    landingPages.forEach((lp) => { if (lp.id != null) map.set(lp.id, lp); });
    return map;
  }, [landingPages]);

  const loadLeads = useCallback(async (landingId: number, page: number) => {
    if (!landingId) return;
    const reqId = ++requestRef.current;
    setLeadsLoading(true);
    try {
      const res = await listLandingLeadsForCurrentCustomer(landingId, { page, size: LEADS_PER_PAGE });
      if (requestRef.current !== reqId) return;
      const landing = landingById.get(landingId);
      const loadedLeads: LeadListItem[] = (res as any)?.content?.map((lead: LandingLeadDTO) => ({
        ...lead,
        landingPageId: landingId,
        landingName: landing?.name,
        slug: landing?.slug,
      })) ?? [];
      setTotalLeads((res as any)?.totalElements ?? 0);
      setLeads(loadedLeads);
      setCurrentPage(page);
    } catch {
      if (requestRef.current !== reqId) return;
      setLeads([]);
    } finally {
      if (requestRef.current === reqId) setLeadsLoading(false);
    }
  }, [landingById]);

  useEffect(() => {
    if (currentLanding?.id) {
      setCurrentPage(0);
      void loadLeads(currentLanding.id, 0);
    } else {
      setLeads([]);
      setTotalLeads(0);
    }
  }, [currentLanding?.id, loadLeads]);

  const totalPages = Math.ceil(totalLeads / LEADS_PER_PAGE);
  const showPagination = totalLeads > LEADS_PER_PAGE;

  const goToPrevPage = () => {
    if (currentPage > 0 && currentLanding?.id) {
      void loadLeads(currentLanding.id, currentPage - 1);
    }
  };
  const goToNextPage = () => {
    if (currentPage < totalPages - 1 && currentLanding?.id) {
      void loadLeads(currentLanding.id, currentPage + 1);
    }
  };

  const goToPrevLanding = () => { if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1); };
  const goToNextLanding = () => { if (selectedIndex < landingPages.length - 1) setSelectedIndex(selectedIndex + 1); };

  const formatLeadDate = (iso?: string | null) => {
    if (!iso) return '';
    const date = dayjs(iso);
    const diffDays = dayjs().diff(date, 'day');
    if (diffDays === 0) return date.format('HH:mm');
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays}g fa`;
    return date.format('DD/MM');
  };

  if (loading) {
    return <Card className="landing-lead-card" bordered={false}><div className="llc-loading"><Spin /></div></Card>;
  }
  if (landingPages.length === 0) return null;

  const stats = currentLanding ? {
    views: currentLanding.totalViews ?? 0,
    leads: currentLanding.totalSubmissions ?? 0,
    redeemed: currentLanding.totalCouponRedeemed ?? 0,
  } : { views: 0, leads: 0, redeemed: 0 };

  return (
    <Card className="landing-lead-card" bordered={false}>
      {/* Header */}
      <div className="llc-header">
        <div className="llc-title-row">
          <div className="llc-icon-title">
            <div className="llc-icon">
              <RocketOutlined />
            </div>
            <div>
              <Title level={4} className="llc-title">
                Landing Pages
              </Title>
              <Text className="llc-subtitle">
                Statistiche e lead delle tue campagne landing.
              </Text>
            </div>
          </div>
        </div>

        {/* Landing selector - solo se multiple */}
        {hasMultipleLandings && (
          <div className="llc-landing-nav">
            <button className="llc-nav-btn" onClick={goToPrevLanding} disabled={selectedIndex === 0}><LeftOutlined /></button>
            <div className="llc-landing-info">
              <Text className="llc-landing-name">{currentLanding?.name || `Landing ${selectedIndex + 1}`}</Text>
              <Text className="llc-landing-counter">{selectedIndex + 1} / {landingPages.length}</Text>
            </div>
            <button className="llc-nav-btn" onClick={goToNextLanding} disabled={selectedIndex === landingPages.length - 1}><RightOutlined /></button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="llc-stats">
        <div className="llc-stat">
          <div className="llc-stat-icon llc-stat-views"><UserOutlined /></div>
          <div className="llc-stat-content">
            <span className="llc-stat-value">{stats.views}</span>
            <span className="llc-stat-label">Visite</span>
          </div>
        </div>
        <div className="llc-stat">
          <div className="llc-stat-icon llc-stat-leads"><MailOutlined /></div>
          <div className="llc-stat-content">
            <span className="llc-stat-value">{stats.leads}</span>
            <span className="llc-stat-label">Lead</span>
          </div>
        </div>
        <div className="llc-stat">
          <div className="llc-stat-icon llc-stat-coupons"><GiftOutlined /></div>
          <div className="llc-stat-content">
            <span className="llc-stat-value">{stats.redeemed}</span>
            <span className="llc-stat-label">Coupon</span>
          </div>
        </div>
      </div>

      {/* Lead list */}
      <div className="llc-lead-section">
        <div className="llc-lead-header">
          <Text className="llc-lead-title">Lead recenti</Text>
          {showPagination && (
            <Text className="llc-lead-page-info">{currentPage + 1} / {totalPages}</Text>
          )}
        </div>

        <div className="llc-lead-list">
          {leadsLoading ? (
            <div className="llc-lead-loading"><Spin size="small" /></div>
          ) : leads.length === 0 ? (
            <div className="llc-lead-empty">
              <MailOutlined className="llc-lead-empty-icon" />
              <Text className="llc-lead-empty-text">Nessun lead</Text>
            </div>
          ) : (
            leads.map((lead, idx) => {
              const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
              return (
                <div key={`${lead.id}-${idx}`} className={`llc-lead-item ${lead.couponUsed ? 'llc-lead-redeemed' : ''}`}>
                  {/* Avatar */}
                  <div className="llc-lead-avatar">
                    {name ? name.charAt(0).toUpperCase() : '?'}
                    {lead.couponUsed && (
                      <div className="llc-lead-badge"><CheckCircleFilled /></div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="llc-lead-info">
                    <span className="llc-lead-name">{name || 'Lead'}</span>
                    <div className="llc-lead-contacts">
                      {lead.email && <span className="llc-lead-contact"><MailOutlined />{lead.email}</span>}
                      {lead.phoneNumber && <span className="llc-lead-contact"><PhoneOutlined />{lead.phoneNumber}</span>}
                    </div>
                  </div>

                  {/* Time + Coupon flag */}
                  <div className="llc-lead-meta">
                    {lead.couponUsed && <span className="llc-coupon-flag"><GiftOutlined /></span>}
                    <span className="llc-lead-time">{formatLeadDate(lead.createdAt)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination - solo se necessaria */}
        {showPagination && (
          <div className="llc-pagination">
            <button className="llc-page-btn" onClick={goToPrevPage} disabled={currentPage === 0}>
              <LeftOutlined />
            </button>
            <div className="llc-page-dots">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageIndex = i;
                if (totalPages > 5) {
                  if (currentPage < 3) pageIndex = i;
                  else if (currentPage > totalPages - 3) pageIndex = totalPages - 5 + i;
                  else pageIndex = currentPage - 2 + i;
                }
                return (
                  <span
                    key={pageIndex}
                    className={`llc-page-dot ${pageIndex === currentPage ? 'active' : ''}`}
                    onClick={() => currentLanding?.id && loadLeads(currentLanding.id, pageIndex)}
                  />
                );
              })}
            </div>
            <button className="llc-page-btn" onClick={goToNextPage} disabled={currentPage >= totalPages - 1}>
              <RightOutlined />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
};

// ============================================================
// CONSULENZA FILES CARD
// ============================================================

const ConsultingFilesCard: React.FC<{ customerId: number }> = ({ customerId }) => {
  const [files, setFiles] = useState<CustomerFileDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [openingId, setOpeningId] = useState<number | null>(null);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCustomerFiles(customerId, 0, 50);
      setFiles(res?.content ?? []);
    } catch {
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!customerId) return;
    fetchFiles();
  }, [customerId, fetchFiles]);

  const handleDownload = async (file: CustomerFileDTO) => {
    setDownloadingId(file.id);
    try {
      const blob = await downloadCustomerFileBlob(customerId, file.id);
      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename || 'file';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpen = async (file: CustomerFileDTO) => {
    setOpeningId(file.id);
    try {
      const blob = await downloadCustomerFileBlob(customerId, file.id);
      const url = window.URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <Card
      bordered={false}
      style={{
        marginBottom: 20,
        borderRadius: 24,
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div
        style={{
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(236, 72, 153, 0.06) 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
        }}
      >
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            color: '#fff',
            boxShadow: '0 6px 20px rgba(239, 68, 68, 0.35)',
            animation: 'trophy-bounce 2s ease-in-out infinite',
          }}
        >
          <FilePdfOutlined />
        </div>
        <div>
          <Title level={4} style={{ margin: 0, fontSize: 17 }}>
            File consulenze
          </Title>
          <Text style={{ fontSize: 13, color: '#6b7280' }}>
            Scarica i recap della tua consulenza.
          </Text>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : files.length === 0 ? (
        <div style={{ padding: '12px 20px 20px' }}>
          <Empty description="Nessun file disponibile" />
        </div>
      ) : (
        <div style={{ padding: '12px 20px 20px' }}>
          <List
            dataSource={files}
            rowKey={(file) => String(file.id)}
            renderItem={(file) => (
              <List.Item
                style={{
                  borderRadius: 12,
                  background: '#fff',
                  border: '1px solid rgba(0, 0, 0, 0.04)',
                  padding: '12px 14px',
                  marginBottom: 10,
                }}
                actions={[
                  <Space key="actions" size={8}>
                    <Button
                    icon={<EyeOutlined />}
                      size="small"
                      onClick={() => handleOpen(file)}
                      loading={openingId === file.id}
                      style={{
                        borderRadius: 10,
                        border: 'none',
                        height: 30,
                        padding: '0 14px',
                        fontWeight: 700,
                        color: '#fff',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
                        marginRight: 10,
                      }}
                    >
                      Apri
                    </Button>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloadingId === file.id}
                      onClick={() => handleDownload(file)}
                      style={{
                        borderRadius: 10,
                        height: 30,
                        width: 30,
                        padding: '0 12px',
                        fontWeight: 600,
                        borderColor: 'rgba(239, 68, 68, 0.35)',
                        color: '#dc2626',
                      }}
                    >
                      
                    </Button>
                  </Space>,
                ]}
              >
                <List.Item.Meta
                  title={<Text strong>{file.filename}</Text>}
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {file.uploadedAt ? dayjs(file.uploadedAt).format('DD/MM/YYYY') : '-'}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </div>
      )}
    </Card>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const CustomerHome: React.FC = () => {
  const [animatedVisibility, setAnimatedVisibility] = useState(0);
  const [currentVisibility, setCurrentVisibility] = useState(0);
  const [viewsSeries, setViewsSeries] = useState<ViewsChartPoint[]>([]);
  const [activities, setActivities] = useState<ActivityDTO[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [news, setNews] = useState<NewsDTO[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [loadingLanding, setLoadingLanding] = useState(false);
  const [landingPages, setLandingPages] = useState<LandingPageDTO[]>([]);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const safeTime = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);
  const timeAgoIT = (iso?: string | null) => {
    if (!iso) return '-';
    const date = new Date(iso), now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const m = 60000, h = 3600000, d = 86400000;
    if (diffMs < h) return `${Math.max(1, Math.floor(diffMs / m))}m fa`;
    if (diffMs < d) return `${Math.floor(diffMs / h)}h fa`;
    const days = Math.floor(diffMs / d);
    if (days === 1) return 'Ieri';
    if (days < 7) return `${days}g fa`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  const getActivityDate = (a: ActivityDTO): string | null => (a as any).createdAt ?? (a as any).occurredAt ?? null;

  useEffect(() => {
    if (!user?.customerId) return;
    setLoadingActivities(true);
    listActivitiesByCustomer(user.customerId, 0, 50)
      .then(r => setActivities(r.content || []))
      .catch(() => setActivities([]))
      .finally(() => setLoadingActivities(false));
  }, [user?.customerId]);

  useEffect(() => {
    setLoadingNews(true);
    listNews()
      .then(p => {
        const content = (Array.isArray(p) ? p : p?.content ?? []) as NewsDTO[];
        setNews([...content].sort((a, b) => safeTime(b.createdAt) - safeTime(a.createdAt)));
      })
      .catch(() => setNews([]))
      .finally(() => setLoadingNews(false));
  }, []);

  useEffect(() => {
    if (!user?.customerId) return;
    listViewsByCustomer(user.customerId, 0, 200)
      .then(p => {
        const arr = p?.content ?? [];
        let total = 0;
        const ordered = [...arr].sort((a, b) => (a.createdAt ? dayjs(a.createdAt).valueOf() : 0) - (b.createdAt ? dayjs(b.createdAt).valueOf() : 0));
        const cumulative = ordered.map((item, idx) => {
          total += Number.isFinite(Number(item?.value)) ? Number(item.value) : 0;
          return { label: item.createdAt ? dayjs(item.createdAt).format('DD MMM') : `#${item.id}`, value: total, key: item.id ? String(item.id) : `${idx}` };
        });
        setCurrentVisibility(total);
        setViewsSeries(cumulative.slice(-5));
      })
      .catch(() => { setCurrentVisibility(0); setViewsSeries([]); });
  }, [user?.customerId]);

  useEffect(() => {
    if (!user?.customerId) return;
    setLoadingLanding(true);
    listLandingPagesForCurrentCustomer({ page: 0, size: 200 })
      .then(res => {
        const pages = (res as any)?.content ?? (Array.isArray(res) ? res : []);
        setLandingPages(pages.filter((p: LandingPageDTO) => p.customerId === user.customerId));
      })
      .catch(() => setLandingPages([]))
      .finally(() => setLoadingLanding(false));
  }, [user?.customerId]);

  const handleLogout = useCallback(async () => {
    try { await logout(); navigate('/login', { replace: true }); } catch {}
  }, [logout, navigate]);

  useEffect(() => {
    const duration = 2000, steps = 80, inc = currentVisibility / steps;
    let current = 0;
    setAnimatedVisibility(0);
    const timer = setInterval(() => {
      current += inc;
      if (current >= currentVisibility) { setAnimatedVisibility(currentVisibility); clearInterval(timer); }
      else setAnimatedVisibility(Math.floor(current));
    }, Math.max(10, duration / steps));
    return () => clearInterval(timer);
  }, [currentVisibility]);

  const postActivities = useMemo(() => activities.filter(a => a.activityType?.label?.toUpperCase().includes('POST') || a.activityType?.code?.toUpperCase().includes('POST')), [activities]);
  const adsActivities = useMemo(() => activities.filter(a => a.activityType?.label?.toUpperCase().includes('ADS') || a.activityType?.code?.toUpperCase().includes('ADS')), [activities]);

  const getActivityIcon = (a: ActivityDTO) => {
    const code = a.activityType?.code || '', label = a.activityType?.label || '';
    if (code.includes('POST') || label.includes('POST')) return <MessageOutlined />;
    if (code.includes('ADS') || label.includes('ADS')) return <RocketOutlined />;
    if (code.includes('IG')) return <InstagramOutlined />;
    return <ThunderboltOutlined />;
  };
  const getActivityColor = (a: ActivityDTO) => {
    const code = a.activityType?.code || '', label = a.activityType?.label || '';
    if (code.includes('POST') || label.includes('POST')) return '#6366f1';
    if (code.includes('ADS') || label.includes('ADS')) return '#ec4899';
    if (code.includes('IG')) return '#8b5cf6';
    return '#06b6d4';
  };

  // =========================
  // NEXT SHOOTING (FUTURE)
  // =========================
  const nextShooting = useMemo(() => {
    const now = dayjs();

    const isShooting = (a: ActivityDTO) => {
      const code = (a.activityType?.code ?? '').toUpperCase();
      const label = (a.activityType?.label ?? '').toUpperCase();
      return code === 'MEDIA_SHOOT' || code.includes('MEDIA_SHOOT') || label === 'SHOOTING' || label.includes('SHOOT');
    };

    const startIso = (a: ActivityDTO): string | null => {
      const apptStart = (a as any)?.appointment?.startAt ?? null;
      const occurred = (a as any)?.occurredAt ?? null;
      return apptStart || occurred || null;
    };

    const upcoming = activities
      .filter(a => isShooting(a))
      .map(a => ({ a, start: startIso(a) }))
      .filter(x => x.start && dayjs(x.start as string).isAfter(now)) // anche 1 minuto dopo
      .sort((x, y) => dayjs(x.start as string).valueOf() - dayjs(y.start as string).valueOf());

    return upcoming.length ? upcoming[0].a : null;
  }, [activities]);

  const formatDateTimeIT = (iso?: string | null) => {
    if (!iso) return '-';
    const d = dayjs(iso);
    return d.format('DD/MM/YYYY • HH:mm');
  };

  const formatDuration = (startIso?: string | null, endIso?: string | null) => {
    if (!startIso || !endIso) return '-';
    const s = dayjs(startIso);
    const e = dayjs(endIso);
    const minutes = e.diff(s, 'minute');
    if (!Number.isFinite(minutes) || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h <= 0) return `${m} min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const featured = news[0] || null;
  const moreNews = news.slice(1, 4);

  const nextShootTitle =
    (nextShooting as any)?.appointment?.title ??
    (nextShooting as any)?.name ??
    'Shooting';

  const nextShootStart =
    (nextShooting as any)?.appointment?.startAt ??
    (nextShooting as any)?.occurredAt ??
    null;

  const nextShootEnd =
    (nextShooting as any)?.appointment?.endAt ??
    null;

  const nextShootLocation =
    (nextShooting as any)?.appointment?.location ??
    null;

  const nextShootMaker =
    (nextShooting as any)?.appointment?.videomakerName ??
    null;

  const nextShootNote =
    (nextShooting as any)?.description ??
    (nextShooting as any)?.appointment?.description ??
    null;

  return (
    <div className="customer-home-v2">
      <div className="floating-shapes"><div className="shape shape-1" /><div className="shape shape-2" /><div className="shape shape-3" /></div>
      <div className="container-v2">
        {/* Header */}
        <div className="header-v2">
          <div className="header-content-v2">
            <div className="user-info">
              <div className="user-details">
                <Text className="welcome-label">Bentornato,</Text>
                <Title level={1} className="user-name">{user?.customerName || 'Dashboard'}</Title>
              </div>
            </div>
            <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} className="logout-button-v2" />
          </div>
        </div>

        {/* Stats */}
        <Card className="visibility-card-v2" bordered={false} style={{ marginBottom: 20 }}>
          <Row gutter={[32, 32]}>
            <Col xs={24} md={14}>
              <div className="visibility-content">
                <div className="visibility-header" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <Title level={4} className="visibility-title">Visualizzazioni</Title>
                    <Text className="visibility-subtitle">Contenuti riprodotti o visualizzati</Text>
                  </div>
                  <div className="trophy-icon"><TrophyOutlined /></div>
                </div>

                <div className="counter-display">
                  <div className="counter-number">{animatedVisibility.toLocaleString()}</div>
                </div>

                <div style={{ marginLeft: 24 }}>
                  <ViewsTrendChart series={viewsSeries} />
                </div>

                
{nextShooting && (
  <div className="next-shooting-inline">
    {/* Header */}
    <div className="nsi-header">
      <div className="nsi-icon">
        <VideoCameraOutlined />
      </div>
      <div className="nsi-header-text">
        <Text className="nsi-kicker">Prossimo shooting</Text>
        <Text className="nsi-title">{nextShootTitle}</Text>
      </div>
    </div>

    {/* Data e ora - pill separato */}
    <div className="nsi-datetime">
      <ClockCircleOutlined className="nsi-datetime-icon" />
      <span className="nsi-datetime-text">{formatDateTimeIT(nextShootStart)}</span>
    </div>

    {/* Grid info */}
    <div className="nsi-grid">
      {/* Location */}
      {nextShootLocation && (
        <div className="nsi-card">
          <div className="nsi-card-icon nsi-icon-location">
            <EnvironmentOutlined />
          </div>
          <div className="nsi-card-text">
            <div className="nsi-card-label">Luogo</div>
            <div className="nsi-card-value">{nextShootLocation}</div>
          </div>
        </div>
      )}

      {/* Videomaker */}
      {nextShootMaker && (
        <div className="nsi-card">
          <div className="nsi-card-icon nsi-icon-maker">
            <UserOutlined />
          </div>
          <div className="nsi-card-text">
            <div className="nsi-card-label">Videomaker</div>
            <div className="nsi-card-value">{nextShootMaker}</div>
          </div>
        </div>
      )}

      {/* Durata */}
      <div className="nsi-card">
        <div className="nsi-card-icon nsi-icon-duration">
          <ClockCircleOutlined />
        </div>
        <div className="nsi-card-text">
          <div className="nsi-card-label">Durata</div>
          <div className="nsi-card-value">{formatDuration(nextShootStart, nextShootEnd)}</div>
        </div>
      </div>
    </div>

    {/* Note - full width se presente */}
    {nextShootNote && String(nextShootNote).trim() && (
      <div className="nsi-note">
        <div className="nsi-note-label">Note</div>
        <div className="nsi-note-value">
          {String(nextShootNote).length > 100 
            ? `${String(nextShootNote).slice(0, 100).trim()}...` 
            : nextShootNote}
        </div>
      </div>
    )}
  </div>
)}
              </div>
            </Col>

            <Col xs={24} md={10}>
              <div className="metrics-grid">
                <div className="metric-item">
                  <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #06b6d4, #0891b2)' }}><LineChartOutlined /></div>
                  <div className="metric-info"><Text className="metric-label">Attività</Text><div className="metric-value">{activities.length}</div></div>
                </div>
                <div className="metric-item">
                  <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #ec4899, #f43f5e)' }}><AreaChartOutlined /></div>
                  <div className="metric-info"><Text className="metric-label">ADV</Text><div className="metric-value">{adsActivities.length}</div></div>
                </div>
                <div className="metric-item">
                  <div className="metric-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}><InstagramOutlined /></div>
                  <div className="metric-info"><Text className="metric-label">Post</Text><div className="metric-value">{postActivities.length}</div></div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {user?.customerId && (
          <ConsultingFilesCard customerId={user.customerId} />
        )}

        {/* Landing & Lead Card */}
        <LandingLeadCard landingPages={landingPages} loading={loadingLanding} />

        {/* Featured News */}
        <Card className="featured-news-card" bordered={false} onClick={() => featured && navigate(`/news/${featured.id}`)} bodyStyle={{ padding: 12 }}>
          <div className="featured-badge"><FireOutlined /> In Evidenza</div>
          {loadingNews ? <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div> : !featured ? <div style={{ padding: 32 }}><Empty description="Nessuna news" /></div> : (
            <Row gutter={[32, 32]} align="middle">
              <Col xs={24} lg={14}>
                <div style={{ height: 260, overflow: 'hidden', borderRadius: 12 }}>
                  <AuthImage alt={featured.title} src={featured.imageUrl || FALLBACK_IMG} style={{ width: '100%', height: '100%', objectFit: 'cover' }} height={260} preview={false} />
                </div>
              </Col>
              <Col xs={24} lg={10}>
                <div className="featured-content">
                  <Title level={3} className="featured-title">{featured.title}</Title>
                  {featured.content && <Paragraph className="featured-excerpt">{truncateNewsContent(featured.content)}</Paragraph>}
                  <div className="featured-meta">
                    <Space size={20}>
                      {featured.author && (
                        <Space size={6}>
                          <UserOutlined className="meta-icon" />
                          <Text className="meta-text">{featured.author}</Text>
                        </Space>
                      )}
                      <Space size={6}>
                        <ClockCircleOutlined className="meta-icon" />
                        <Text className="meta-text">{timeAgoIT(featured.createdAt)}</Text>
                      </Space>
                    </Space>
                  </div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<ReadOutlined />}
                    className="read-more-btn"
                    onClick={e => { e.stopPropagation(); navigate(`/news/${featured.id}`); }}
                  >
                    Leggi
                  </Button>
                </div>
              </Col>
            </Row>
          )}
        </Card>

        {/* More News */}
        <div className="news-preview-section">
          <div className="section-header-v2">
            <Title level={4} className="section-title-v2">Altre News</Title>
            <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/news')} className="view-all-link">Vedi tutte</Button>
          </div>
          {loadingNews ? <div style={{ padding: 16, textAlign: 'center' }}><Spin /></div> : moreNews.length === 0 ? <Card className="empty-card" bordered={false}><Empty description="Nessuna news" /></Card> : (
            <Row gutter={[16, 16]}>
              {moreNews.map(n => (
                <Col xs={24} sm={12} lg={8} key={n.id}>
                  <Card className="news-preview-card" hoverable bordered={false} onClick={() => navigate(`/news/${n.id}`)}>
                    <div className="news-card-content">
                      <div className="news-color-bar" style={{ background: '#3b82f6' }} />
                      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                        <div style={{ width: 80, height: 80, flexShrink: 0, borderRadius: 8, overflow: 'hidden', backgroundColor: '#f1f5f9' }}>
                          <AuthImage alt={n.title} src={n.imageUrl || FALLBACK_IMG} height={80} preview={false} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Title level={5} className="news-card-title" style={{ marginBottom: 4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{n.title}</Title>
                          {(n.content || n.excerpt) && <Paragraph style={{ marginBottom: 4 }} className="news-card-excerpt">{truncateNewsContent(n.content || n.excerpt)}</Paragraph>}
                          <Text className="news-time">{timeAgoIT(n.createdAt)}</Text>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>

        {/* Activities */}
        <div className="activities-section-v2">
          <div className="section-header-v2">
            <Title level={4} className="section-title-v2">Attività Recenti</Title>
            <Button type="link" icon={<ArrowRightOutlined />} onClick={() => navigate('/activities')} className="view-all-link">Vedi tutte</Button>
          </div>
          {loadingActivities ? <div className="loading-state"><Spin size="large" /></div> : activities.length === 0 ? (
            <Card className="empty-card" bordered={false}><Empty description={<Space direction="vertical" size={8}><Text className="empty-title">Nessuna attività</Text></Space>} /></Card>
          ) : (
            <Row gutter={[16, 16]}>
              {activities.slice(0, 6).map(activity => {
                const when = getActivityDate(activity);
                const formatTime = (iso?: string | null) => {
                  if (!iso) return '-';
                  const diffMs = Date.now() - new Date(iso).getTime();
                  if (diffMs < 3600000) return 'Ora';
                  if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h`;
                  const days = Math.floor(diffMs / 86400000);
                  if (days === 1) return 'Ieri';
                  if (days < 7) return `${days}g`;
                  return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
                };
                return (
                  <Col xs={12} sm={12} lg={8} key={activity.id}>
                    <Card className="activity-card-v2" hoverable bordered={false} onClick={() => navigate(`/activities/${activity.id}`)}>
                      <div className="activity-card-inner">
                        <div className="activity-header-v2">
                          <Avatar icon={getActivityIcon(activity)} size={48} style={{ background: getActivityColor(activity) }} className="activity-avatar-v2" />
                          <Text className="activity-time-v2">{formatTime(when)}</Text>
                        </div>
                        <Title level={5} className="activity-title-v2">{activity.name.length > 40 ? `${activity.name.slice(0, 40)}...` : activity.name}</Title>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerHome;
